import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router";
import { Box, Button, Container, TextField, Typography, Paper, Alert, Stack } from "@mui/material";
import useAuth from "../hooks/useAuth";
import AuthService from "../api/axiosInstance";
import endpoints from "../api/endpoints";

export default function LoginPage() {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const next = searchParams.get("next");

  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [errorKind, setErrorKind] = useState(null);
  const [resendMessage, setResendMessage] = useState(null);
  const [resendSending, setResendSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState(null);
  const [resetSubmitted, setResetSubmitted] = useState(false);

  useEffect(() => {
    if (mode === "login") {
      setError(null);
      setErrorKind(null);
      setResendMessage(null);
    } else {
      setResetMessage(null);
      setResetSubmitted(false);
    }
  }, [mode]);

  const handleChange = (e) => {
    if (e.target.name === "email") {
      setResendMessage(null);
    }
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (mode === "reset") {
      await handleResetRequest();
      return;
    }
    if (!form.email || !form.password) {
      setError("Please enter both email and password.");
      setErrorKind(null);
      return;
    }
    setSubmitting(true);
    try {
      await login(form);
      const target = next || "/";
      navigate(target);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Invalid email or password. If you recently registered, please verify your email.";
      const isUnverified =
        typeof detail === "string" && detail.toLowerCase().includes("verify your email to continue");
      setError(
        isUnverified
          ? "Please verify your email to continue. Resend confirmation email?"
          : detail
      );
      setErrorKind(isUnverified ? "unverified" : null);
      setResendMessage(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetRequest = async () => {
    if (!resetEmail) {
      setResetMessage("Please enter your email.");
      return;
    }
    try {
      await AuthService.post(endpoints.PASSWORD_RESET, { email: resetEmail });
      setResetMessage("If that email exists, a reset link has been sent.");
      setResetSubmitted(true);
    } catch {
      setResetMessage("Unable to send reset link. Please try again.");
    }
  };

  const handleResendVerification = async () => {
    if (!form.email) {
      setResendMessage("Please enter your email above to resend the confirmation.");
      return;
    }
    setResendSending(true);
    setResendMessage(null);
    try {
      const res = await AuthService.post(endpoints.VERIFY_EMAIL_RESEND, { email: form.email });
      setResendMessage(res?.data?.detail || "Verification link sent.");
    } catch {
      setResendMessage("Unable to send verification email. Please try again.");
    } finally {
      setResendSending(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f7f9fc", minHeight: "100vh", py: { xs: 6, md: 10 } }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={800} gutterBottom>
            {mode === "login" ? "Sign in" : "Reset password"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {mode === "login"
              ? "Use your account email and password to sign in."
              : "Enter your account email and we'll send you a reset link."}
          </Typography>

          {mode === "login" && error && (
            <Alert
              severity="error"
              action={
                errorKind === "unverified" ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleResendVerification}
                    disabled={resendSending}
                  >
                    {resendSending ? "Sending..." : "Resend"}
                  </Button>
                ) : null
              }
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}
          {mode === "login" && resendMessage && (
            <Typography color="text.secondary">{resendMessage}</Typography>
          )}
          {mode === "reset" && resetMessage && (
            <Typography color={resetSubmitted ? "success.main" : "error"}>{resetMessage}</Typography>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ mt: 2 }}>
              {mode === "login" ? (
                <>
                  <TextField
                    label="Email"
                    name="email"
                    fullWidth
                    required
                    value={form.email}
                    onChange={handleChange}
                    autoComplete="email"
                    autoFocus
                  />
                  <TextField
                    label="Password"
                    name="password"
                    type="password"
                    fullWidth
                    required
                    value={form.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                  />
                  <Button variant="contained" type="submit" disabled={submitting}>
                    {submitting ? "Signing in..." : "Sign In"}
                  </Button>
                  {errorKind !== "unverified" && (
                    <Button
                      onClick={() => {
                        setMode("reset");
                        setForm({ email: "", password: "" });
                        setError(null);
                        setErrorKind(null);
                        setResetMessage(null);
                        setResetSubmitted(false);
                      }}
                    >
                      Forgot password?
                    </Button>
                  )}
                  <Button component={RouterLink} to="/">
                    Back to home
                  </Button>
                </>
              ) : (
                <>
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    autoComplete="email"
                    autoFocus
                  />
                  <Button variant="contained" onClick={handleResetRequest} disabled={submitting}>
                    Send Reset Link
                  </Button>
                    <Button
                      onClick={() => {
                        setMode("login");
                        setForm({ email: "", password: "" });
                        setResetMessage(null);
                        setResetSubmitted(false);
                        setError(null);
                        setErrorKind(null);
                      }}
                  >
                    Back to login
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
